import React, { useState, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { mockTestimonials } from '../mockData';

const Testimonials = () => {
  const scrollContainerRef = useRef(null);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = direction === 'left' ? -450 : 450;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6 sm:mb-[42px]">
          <div>
            <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3" data-testid="testimonials-kicker">
              Testimonials
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-[#0a1929] leading-tight tracking-tight">
              Why merchants choose eCOMM.
            </h2>
          </div>

          {/* Navigation Arrows */}
          <div className="hidden md:flex gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => scroll('left')}
              className="group rounded-full border border-gray-200 text-[#1F4E79] bg-white hover:bg-[#1F4E79] hover:text-white hover:border-[#1F4E79] hover:shadow-sm transition-all duration-200 w-10 h-10"
            >
              <ChevronLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={() => scroll('right')}
              className="group rounded-full border border-gray-200 text-[#1F4E79] bg-white hover:bg-[#1F4E79] hover:text-white hover:border-[#1F4E79] hover:shadow-sm transition-all duration-200 w-10 h-10"
            >
              <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>

        {/* Scrollable Testimonials */}
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {mockTestimonials.map((testimonial) => (
            <Card
              key={testimonial.id}
              className={`flex-shrink-0 w-[380px] md:w-[420px] rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 snap-start ${
                testimonial.type === 'image'
                  ? 'border-0 overflow-hidden bg-transparent shadow-none'
                  : 'bg-white border border-gray-100'
              }`}
            >
              <CardContent className={`${testimonial.type === 'image' ? 'p-0 justify-center' : 'p-8 pt-16 md:pt-20 justify-start'} flex flex-col items-center text-center min-h-[280px]`}>
                {testimonial.type === 'image' ? (
                  <img
                    src={testimonial.image}
                    alt="Customer"
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <div className="w-full">
                    <p className="text-base md:text-lg font-normal leading-relaxed mb-6 text-gray-800">
                      {testimonial.quote}
                    </p>

                    <div>
                      <p className="text-sm text-gray-700">
                        {testimonial.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {testimonial.business}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mobile Navigation — bottom arrows for < md (phones only). UNCHANGED. */}
        <div className="flex md:hidden justify-center gap-3 mt-8">
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('left')}
            className="rounded-full border-2 border-[#1F4E79] text-[#1F4E79] hover:bg-[#1F4E79] hover:text-white transition-all duration-200 w-12 h-12"
          >
            <ChevronLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => scroll('right')}
            className="rounded-full border-2 border-[#1F4E79] text-[#1F4E79] hover:bg-[#1F4E79] hover:text-white transition-all duration-200 w-12 h-12"
          >
            <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </Button>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};

export default Testimonials;